import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { SG_GET_PUBLIC_UPLOADS_BY_CATEGORY } from "constants/actions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { FolderOpen, Download, Inbox, Info, Loader2, CheckCircle, Lock } from "lucide-react";

const CATEGORY_OPTIONS = [
  { value: "pdf_template", label: "PDF Template" },
  { value: "logo", label: "Logo" },
  { value: "css", label: "CSS" },
  { value: "js", label: "JavaScript" },
  { value: "json", label: "JSON" },
  { value: "branding", label: "Branding" },
  { value: "document", label: "Document" },
  { value: "image", label: "Image" },
  { value: "other", label: "Other" },
];

export default function UploadsByCategory() {
  const dispatch = useDispatch();
  const [selectedCategory, setSelectedCategory] = useState("logo");
  const [organizationFilter, setOrganizationFilter] = useState("all");

  const { publicUploadsByCategory, loading } = useSelector(
    (state) => state.public_upload
  );

  const { organization_select } = useSelector(
    (state) => state.organizations || {}
  );

  useEffect(() => {
    loadByCategory();
  }, [selectedCategory, organizationFilter]);

  const loadByCategory = () => {
    const params = { category: selectedCategory };
    
    if (organizationFilter && organizationFilter !== "all") {
      params.organization = organizationFilter;
    }

    dispatch({ type: SG_GET_PUBLIC_UPLOADS_BY_CATEGORY, payload: params });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl flex items-center gap-2">
          <FolderOpen className="h-6 w-6" />
          Uploads by Category
        </CardTitle>
        <CardDescription>Browse uploads organized by category</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6 p-4 bg-muted rounded-lg">
          <div className="space-y-2 flex-1 min-w-[200px]">
            <Label htmlFor="category">Category</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger id="category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {organization_select && organization_select.length > 0 && (
            <div className="space-y-2 flex-1 min-w-[200px]">
              <Label htmlFor="organization">Organization</Label>
              <Select value={organizationFilter} onValueChange={setOrganizationFilter}>
                <SelectTrigger id="organization">
                  <SelectValue placeholder="All Organizations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Organizations</SelectItem>
                  {organization_select.map((org) => (
                    <SelectItem key={org.id} value={String(org.id)}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Results Count */}
        {publicUploadsByCategory?.count > 0 && (
          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertDescription>
              Found {publicUploadsByCategory.count} file
              {publicUploadsByCategory.count !== 1 ? "s" : ""} in this category
            </AlertDescription>
          </Alert>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>File Type</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead className="text-center">Public</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>File</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {publicUploadsByCategory?.results?.length > 0 ? (
                  publicUploadsByCategory.results.map((upload) => (
                    <TableRow key={upload.id}>
                      <TableCell className="font-medium">{upload.id}</TableCell>
                      <TableCell className="font-medium">{upload.name}</TableCell>
                      <TableCell>
                        {upload.description || (
                          <span className="text-muted-foreground italic">No description</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {upload.file_type || upload.mime_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatFileSize(upload.file_size)}
                      </TableCell>
                      <TableCell>
                        {upload.organization_name || (
                          <Badge variant="outline">Global</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {upload.is_public ? (
                          <CheckCircle className="h-4 w-4 text-green-600 mx-auto" />
                        ) : (
                          <Lock className="h-4 w-4 text-muted-foreground mx-auto" />
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(upload.created_at), "MMM dd, yyyy HH:mm")}
                      </TableCell>
                      <TableCell>
                        <a
                          href={upload.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan="9" className="text-center py-12">
                      <Inbox className="h-12 w-12 mx-auto text-muted-foreground/50" />
                      <h3 className="mt-4 text-lg font-medium text-muted-foreground">
                        No files found in this category
                      </h3>
                      <p className="text-sm text-muted-foreground mt-2">
                        Try selecting a different category or organization
                      </p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
